import { Sites } from '@ps/platform-message-framework/common/Sites';
import { Size } from '@ps/web-core-enums';
import type { Page } from '@ps/web-core-store';
import { useLayout } from '@ps/web-sports-utils';
import classNames from 'clsx';
import React, { useCallback, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { CMSReferences } from '@swf/cms';
import NPS from '@swf/components/NPS/NPS';
import Redirect from '@swf/components/Redirect/Redirect';
import { CacheControl, Vary } from '@swf/components/ResponseHeaders/ResponseHeaders';
import ServiceUnavailable from '@swf/components/ServiceUnavailable/ServiceUnavailable';
import { Header as HeaderEnum } from '@swf/core/enums';
import useHistoryScrollPosition from '@swf/core/hooks/useHistoryScrollPosition';
import useMutationObserver from '@swf/core/hooks/useMutationObserver';
import { useRefs } from '@swf/core/providers/RefsProvider';
import { select } from '@swf/core/store';
import { ExtraWalletsInfoBar } from '@swf/sports/components/ExtraWalletsInfoBar/ExtraWalletsInfoBar';
import SportsProviders from '@swf/sports/providers/SportsProviders';
import DrukCyTTMedium from '@swf/themes/assets/fonts/DrukText-Medium-Cy-Gr-Web.woff2';

import useCheckSportsStatus from '../hooks/useCheckSportsStatus';

import styles from './SportsLayout.scss';
import BetSlip from './components/BetSlip/BetSlipWrapper';
import Header from './components/Header';
import Navigation from './components/Navigation/Navigation';
import Top from './components/Top/Top';
import useSetCssVariable from './hooks/useSetCssVariable';

const mutationObserverHookOptions = {
  attributes: true,
  childList: true,
  subtree: true,
  triggerBeforeMutation: true
};

const SportsLayout: React.FC<Page> = (page) => {
  const { setRefs } = useRefs();
  const website = useSelector(select.cms.getWebsite);
  const [content] = page.content;
  const { showNavigationSection, setShowNavigationSection } = useLayout();
  const layoutRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const isSystemAvailable = useSelector(select.system.isAvailable);
  const isSelfExcluded = useSelector(select.account.getSelfExclusion);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const embeddedRoute = useSelector(select.location.isEmbeddedRoute);
  const showWebContent = !(isEmbedded || embeddedRoute);
  const isBannedByRegulator = useSelector(select.account.bannedByRegulator);
  const isDeSite = useSelector(select.cms.getSiteId) === Sites.DE;
  const isDeBannedByRegulator = isDeSite && isBannedByRegulator;
  const { setCssVariable, setHeightCssVariableForRef } = useSetCssVariable(layoutRef);
  const { pathname } = useLocation();
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);

  useCheckSportsStatus();

  useEffect(() => {
    setShowNavigationSection(false);
  }, [pathname, setShowNavigationSection]);

  useEffect(() => {
    setRefs('sportsLayout', layoutRef);
  }, [setRefs]);

  useEffect(() => {
    setCssVariable(
      '--sportsLayoutScrollTop',
      showNavigationSection ? `${window.scrollY}px` : '0px'
    );
  }, [setCssVariable, showNavigationSection]);

  const setTopCssVariable = useCallback(() => {
    setHeightCssVariableForRef('--sportsLayoutTopHeight', topRef);
  }, [setHeightCssVariableForRef]);

  useMutationObserver(topRef, setTopCssVariable, mutationObserverHookOptions);

  useHistoryScrollPosition();

  if (isSelfExcluded || isDeBannedByRegulator) {
    return <Redirect alias="sportsSelfExcluded" />;
  }

  if (!page.uid) {
    return <Redirect alias="sportsHome" />;
  }

  return (
    <>
      <Helmet>
        <link rel="preload" href={DrukCyTTMedium} as="font" />
      </Helmet>
      <Vary by={[HeaderEnum.OddsFormat]} />
      <CacheControl>public, max-age=5, stale-while-revalidate=60</CacheControl>

      <div id="control-height" className={styles.controlHeight} />

      <div
        className={classNames(styles.layout, {
          [styles.disableLinkPreview]: isMobileEmbedded
        })}
        ref={layoutRef}
      >
        {showWebContent && <Header references={website?.site_header} />}
        {isSystemAvailable ? (
          <>
            <CMSReferences references={content.seo} />

            <Top ref={topRef} references={content.top} />

            <ExtraWalletsInfoBar hiddenFrom={Size.Small} />

            <div className={styles.wrapper}>
              <Navigation navigationSection={content.left} />

              <div className={styles.main}>
                <CMSReferences references={content.main} />
              </div>

              <div className={styles.right}>
                <BetSlip />
              </div>
            </div>

            <NPS />
          </>
        ) : (
          <ServiceUnavailable />
        )}
        <CMSReferences references={website?.site_footer} />
      </div>
    </>
  );
};

const SportsLayoutWithProvider: React.FC<Page> = (props) => (
  <SportsProviders>
    <SportsLayout {...props} />
  </SportsProviders>
);

export default SportsLayoutWithProvider;
